import { defineComponent as _defineComponent } from 'vue'
import { openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, unref as _unref, createVNode as _createVNode, createTextVNode as _createTextVNode, withCtx as _withCtx, createElementVNode as _createElementVNode, normalizeClass as _normalizeClass } from "vue"

const _hoisted_1 = { class: "wizard-container" }

import { ref, reactive, computed, onMounted } from 'vue';
import { useStore } from 'vuex';
import Application from '../models/applications';
import CruResource from '@shell/components/CruResource.vue';
import ResourceTabs from '@shell/components/form/ResourceTabs/index.vue';
import Tab from '@shell/components/Tabbed/Tab.vue';
import Loading from '@shell/components/Loading.vue';
import AppInfo from '../components/application/AppInfo.vue';
import AppConfiguration from '../components/application/AppConfiguration.vue';
import { epinioExceptionToErrorsArray } from '../utils/errors';
import { useEpinioBindAppsMixin } from './bind-apps-mixin';

import Wizard from '@shell/components/Wizard.vue';
import { createEpinioRoute } from '../utils/custom-routing';
import AppSource from '../components/application/AppSource.vue';
import AppProgress from '../components/application/AppProgress.vue';
import { EpinioAppSource, EPINIO_TYPES, EpinioAppInfo, EpinioAppBindings } from '../types';
import { allHash } from '@shell/utils/promise';
import { _EDIT } from '@shell/config/query-params';


export default /*@__PURE__*/_defineComponent({
  __name: 'applications',
  props: {
    value: {},
    initialValue: {},
    mode: {}
  },
  setup(__props: any) {

const props = __props;

const { 
  doneParams,
  doneRoute,
} = useEpinioBindAppsMixin(props);

const store = useStore();

const t = store.getters['i18n/t'];

const bindings = reactive<EpinioAppBindings>({ configurations: [], services: [] });
const source = ref<EpinioAppSource>(props.value.appSource);
const errors = ref<string[]>([]);
const epinioInfo = ref();
const tabErrors = reactive<{ appInfo: boolean }>({ appInfo: false });

const steps = reactive([
  {
    name: 'source',
    label: t('epinio.applications.steps.source.label'),
    subtext: t('epinio.applications.steps.source.subtext'),
    ready: false,
    nextButton: {
      labelKey: 'epinio.applications.steps.configurations.update',
      style: 'btn role-primary bg-warning'
    }
  },
  {
    name: 'progress',
    label: t('epinio.applications.steps.progress.label'),
    subtext: t('epinio.applications.steps.progress.subtext'),
    ready: false,
    previousButton: { disable: true }
  },
]);

if (source.value.git && store.$router.currentRoute._value.query?.commit) {
  source.value.git.commit = store.$router.currentRoute._value.query.commit as string;
}

onMounted(async () => {
  const hash = await allHash({
    ns: store.dispatch('epinio/findAll', { type: EPINIO_TYPES.NAMESPACE }),
    charts: store.dispatch('epinio/findAll', { type: EPINIO_TYPES.APP_CHARTS }),
    info: store.dispatch('epinio/info'),
  });

  epinioInfo.value = (hash as { info: any }).info;
});

const shouldShowButtons = computed(
  () => (store.$router.currentRoute.value.hash === '#source' ? 'hide-buttons-deploy' : '')
);
const showSourceTab = computed(() => {
  return props.mode === _EDIT
});
const validationPassed = computed(() => !Object.values(tabErrors).find((error) => error));

const done = () => {
  if (!doneRoute) {
    return;
  }

  store.$router.replace({
    name:   doneRoute.value,
    params: doneParams.value || { resource: props.value.type },
  });
}


async function save(saveCb: (success: boolean) => void) {
  errors.value = [];
  try {
    await props.value.update();

    await props.value.updateConfigurations(
      props.initialValue.baseConfigurationsNames || [],
      bindings.configurations || [],
    );

    await props.value.updateServices(
      props.initialValue.services || [],
      bindings.services || [],
    );

    await props.value.forceFetch();
    saveCb(true);
    done();
  } catch (err) {
    errors.value = epinioExceptionToErrorsArray(err);
    saveCb(false);
  }
}

function set(obj: Record<string, any>, changes: Record<string, any>) {
  Object.entries(changes).forEach(([key, value]) => {
    obj[key] = value;
  });
}

function updateInfo(changes: EpinioAppInfo) {
  props.value.meta = props.value.meta || {};
  props.value.configuration = props.value.configuration || {};
  set(props.value.meta, changes.meta);
  set(props.value.configuration, changes.configuration);
}

function updateConfigurations(changes: EpinioAppBindings) {
  Object.assign(bindings, changes);
}

function cancel() {
  store.$router.replace(props.value.listLocation);
}

function finish() {
  store.$router.replace(createEpinioRoute(`c-cluster-resource-id`, {
    cluster: store.getters['clusterId'],
    resource: props.value.type,
    id: `${props.value.meta.namespace}/${props.value.meta.name}`,
  }));
}

function updateSource(changes: EpinioAppSource) {
  source.value = {} as EpinioAppSource;
  const { appChart, ...cleanChanges } = changes;

  props.value.configuration = props.value.configuration || {};

  if (appChart) {
    set(props.value.configuration, { appchart: appChart });
  }

  set(source.value, cleanChanges);
}

function updateManifestConfigurations(changes: string[]) {
  set(props.value.configuration, { configurations: changes });
}

function validate(value: boolean, tab: string) {
  if (tab) {
    tabErrors[tab] = !value;
  }

  steps[0].ready = value;
}

return (_ctx: any,_cache: any) => {
  return (!_ctx.value)
    ? (_openBlock(), _createBlock(Loading, { key: 0 }))
    : (_openBlock(), _createBlock(CruResource, {
        key: 1,
        class: _normalizeClass(shouldShowButtons.value),
        "can-yaml": false,
        mode: _ctx.mode,
        resource: _ctx.value,
        errors: errors.value,
        "validation-passed": validationPassed.value,
        onError: _cache[2] || (_cache[2] = (e) => errors.value = _unref(epinioExceptionToErrorsArray)(e)),
        onFinish: save
      }, {
        default: _withCtx(() => [
          _createVNode(ResourceTabs, { mode: "mode" }, {
            default: _withCtx(() => [
              (showSourceTab.value)
                ? (_openBlock(), _createBlock(Tab, {
                    key: 0,
                    "label-key": "epinio.applications.steps.source.label",
                    name: "source",
                    weight: 30
                  }, {
                    default: _withCtx(() => [
                      _createElementVNode("div", _hoisted_1, [
                        _createVNode(Wizard, {
                          class: "wizard",
                          steps: steps,
                          "banner-title": _unref(t)('epinio.applications.steps.source.label'),
                          "banner-title-subtext": _unref(t)('epinio.applications.steps.source.subtext'),
                          "header-mode": "edit",
                          "finish-mode": "done",
                          "edit-first-step": true,
                          onCancel: cancel,
                          onFinish: finish
                        }, {
                          source: _withCtx(() => [
                            (source.value)
                              ? (_openBlock(), _createBlock(AppSource, {
                                  key: 0,
                                  application: _ctx.value,
                                  source: source.value,
                                  mode: _ctx.mode,
                                  info: epinioInfo.value,
                                  onChange: updateSource,
                                  onChangeAppInfo: updateInfo,
                                  onChangeAppConfig: updateManifestConfigurations,
                                  onValid: _cache[0] || (_cache[0] = ($event: any) => (steps[0].ready = $event))
                                }, null, 8, ["application", "source", "mode", "info"]))
                              : _createCommentVNode("", true)
                          ]),
                          progress: _withCtx(({step}) => [
                            _createVNode(AppProgress, {
                              application: _ctx.value,
                              source: source.value,
                              bindings: bindings,
                              mode: _ctx.mode,
                              step: step
                            }, null, 8, ["application", "source", "bindings", "mode", "step"])
                          ]),
                          _: 1,
                          __: [3]
                        }, 8, ["steps", "banner-title", "banner-title-subtext"])
                      ])
                    ]),
                    _: 1
                  }))
                : _createCommentVNode("", true),
              _cache[4] || (_cache[4] = _createTextVNode()),
              _createVNode(Tab, {
                "label-key": "epinio.applications.steps.basics.label",
                name: "info",
                weight: 20,
                error: tabErrors.appInfo
              }, {
                default: _withCtx(() => [
                  _createVNode(AppInfo, {
                    application: _ctx.value,
                    mode: _ctx.mode,
                    onChange: updateInfo,
                    onValid: _cache[1] || (_cache[1] = ($event: any) => (validate($event, 'appInfo')))
                  }, null, 8, ["application", "mode"])
                ]),
                _: 1
              }, 8, ["error"]),
              _cache[5] || (_cache[5] = _createTextVNode()),
              _createVNode(Tab, {
                "label-key": "epinio.applications.steps.configurations.label",
                name: "configurations",
                weight: 10
              }, {
                default: _withCtx(() => [
                  _createVNode(AppConfiguration, {
                    application: _ctx.value,
                    "initial-application": _ctx.initialValue,
                    mode: _ctx.mode,
                    onChange: updateConfigurations
                  }, null, 8, ["application", "initial-application", "mode"])
                ]),
                _: 1
              })
            ]),
            _: 1,
            __: [4,5]
          })
        ]),
        _: 1
      }, 8, ["class", "mode", "resource", "errors", "validation-passed"]))
}
}

})